import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Collapse,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import EventNoteIcon from '@mui/icons-material/EventNote';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import GroupIcon from '@mui/icons-material/Group';
import InventoryIcon from '@mui/icons-material/Inventory';
import LinkIcon from '@mui/icons-material/Link';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import HistoryIcon from '@mui/icons-material/History';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CategoryIcon from '@mui/icons-material/Category';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ScienceIcon from '@mui/icons-material/Science';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUsefulLinks } from '../../context/UsefulLinksContext';
import HorseIcon from '../HorseIcon';
import { canManage, isAdmin, isInventoryCounter } from '../../utils/roles';

const USEFUL_LINKS_MANAGE_PATH = '/admin/useful-links';
const INVENTORY_PATH_PREFIX = '/inventory';
const STOCK_COUNT_PATH = '/inventory/count';
const TIME_CLOCK_PATH = '/time-clock';
const REPORTS_PATH_PREFIX = '/reports';
const EXPENSE_SUMMARY_REPORT_PATH = '/reports/expense-summary';

// Indented child rows. The theme's `.Mui-selected` rule sets its own paddingLeft
// to absorb the 3px left border it adds, and that two-class selector outranks a
// plain `sx` rule — so a selected child would snap back to the top-level indent.
// Restate the indent for the selected state, less the border, so the label holds
// still whether or not the row is selected.
const nestedItemSx = {
  pl: '32px',
  '&.Mui-selected': { pl: '29px' },
};

const inventoryMenuItems = [
  { text: 'Items', icon: <InventoryIcon />, path: INVENTORY_PATH_PREFIX },
  { text: 'Stock Count', icon: <InventoryIcon />, path: STOCK_COUNT_PATH },
];

const reportsMenuItems = [
  { text: 'Tax Workbook', icon: <DescriptionIcon />, path: REPORTS_PATH_PREFIX },
  { text: 'Expense Summary', icon: <AssessmentIcon />, path: EXPENSE_SUMMARY_REPORT_PATH },
];

const dailyMenuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Ranch Assistant', icon: <SmartToyIcon />, path: '/assistant' },
  { text: 'Horses', icon: <HorseIcon />, path: '/horses' },
  { text: 'Breeding', icon: <FavoriteIcon />, path: '/breeding' },
];

const recordsMenuItems = [
  { text: 'Appointments', icon: <CalendarMonthIcon />, path: '/appointments' },
  { text: 'Feed', icon: <RestaurantIcon />, path: '/feed' },
  { text: 'Inventory', icon: <InventoryIcon />, children: inventoryMenuItems },
  { text: 'Expenses', icon: <AttachMoneyIcon />, path: '/expenses' },
  { text: 'Income', icon: <AccountBalanceWalletIcon />, path: '/income' },
  { text: 'Reports', icon: <AssessmentIcon />, children: reportsMenuItems },
  { text: 'Shows', icon: <EmojiEventsIcon />, path: '/shows' },
];

// Lives in Records rather than Admin, but is admin/manager only: the entries
// hold prospective customers' contact details, and every /api/waitlist route is
// @manager_required, so anyone else would only reach a page of 403s.
const waitlistMenuItem = {
  text: 'Foal Waitlist', icon: <PlaylistAddCheckIcon />, path: '/waitlist',
};

const geneticsMenuItems = [
  { text: 'Hypothetical Pedigree', icon: <ScienceIcon />, path: '/genetics/hypothetical-pedigree' },
];

// The Admin section is admin-only, manager included. A manager may rename and
// remove reference data at the API level, but reaches it through the "+ Add
// new" affordances in the everyday forms rather than through these pages.
const adminMenuItems = [
  { text: 'Business Info', icon: <BusinessIcon />, path: '/admin/business' },
  { text: 'Users', icon: <GroupIcon />, path: '/admin/users' },
  { text: 'Inventory', icon: <InventoryIcon />, path: '/admin/inventory' },
  { text: 'Time Clock', icon: <AccessTimeIcon />, path: '/admin/time-clock' },
  { text: 'Expense Types', icon: <CategoryIcon />, path: '/admin/expense-types' },
  { text: 'Expense Locations', icon: <LocationOnIcon />, path: '/admin/expense-locations' },
  { text: 'Income Types', icon: <CategoryIcon />, path: '/admin/income-types' },
  { text: 'Income Locations', icon: <LocationOnIcon />, path: '/admin/income-locations' },
  { text: 'Mileage Rates', icon: <DirectionsCarIcon />, path: '/admin/mileage-rates' },
  { text: 'Personal Inbox Filters', icon: <MarkEmailReadIcon />, path: '/admin/personal-inbox-filters' },
  { text: 'Horse Pedigrees', icon: <HorseIcon />, path: '/admin/horse-pedigrees' },
  { text: 'AllBreed Submissions', icon: <HorseIcon />, path: '/admin/allbreed' },
  { text: 'Mare Owners', icon: <GroupIcon />, path: '/mare-owners' },
  { text: 'Vets', icon: <MedicalServicesIcon />, path: '/vets' },
  { text: 'Trainers', icon: <FitnessCenterIcon />, path: '/trainers' },
  { text: 'Appointment Types', icon: <EventNoteIcon />, path: '/appointment-types' },
  { text: 'System Status', icon: <MonitorHeartIcon />, path: '/admin/system-status' },
  { text: 'Audit Log', icon: <HistoryIcon />, path: '/admin/audit-log' },
];

const Sidebar = ({ drawerWidth, mobileOpen, onDrawerToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { links: usefulLinks } = useUsefulLinks();
  const [adminOpen, setAdminOpen] = useState(false);
  // Seeded from the route so a deep link into a nested section (e.g. Inventory,
  // Reports) mounts with its parent already expanded, rather than hiding the
  // item the user landed on. Keyed by menu item text since multiple top-level
  // items can have children, each expanding independently.
  const [openSections, setOpenSections] = useState(() => ({
    Inventory: location.pathname.startsWith(INVENTORY_PATH_PREFIX),
    Reports: location.pathname.startsWith(REPORTS_PATH_PREFIX),
  }));

  const handleNavigation = (path) => {
    navigate(path);
    if (mobileOpen) {
      onDrawerToggle();
    }
  };

  // Useful links open in a new tab via the anchor's own href, so there is no
  // navigate() call here — only the mobile drawer needs dismissing.
  const handleExternalLinkClick = () => {
    if (mobileOpen) {
      onDrawerToggle();
    }
  };

  const handleAdminToggle = () => {
    setAdminOpen(!adminOpen);
  };

  const handleSectionToggle = (text) => {
    setOpenSections((prev) => ({ ...prev, [text]: !prev[text] }));
  };

  const admin = isAdmin(user);
  const counter = isInventoryCounter(user);

  const recordsItems = canManage(user)
    ? [...recordsMenuItems, waitlistMenuItem]
    : recordsMenuItems;

  // Hide the section entirely from a non-admin when there is nothing in it —
  // they have no way to add one, so an empty header is just noise.
  const showUsefulLinks = usefulLinks.length > 0 || admin;

  const counterMenuItems = [
    inventoryMenuItems.find((item) => item.path === STOCK_COUNT_PATH),
    { text: 'Time Clock', icon: <AccessTimeIcon />, path: TIME_CLOCK_PATH },
  ];

  const drawer = counter ? (
    <div>
      <Toolbar />
      <List dense>
        {counterMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  ) : (
    <div>
      <Toolbar />

      {/* Daily */}
      <Box sx={{ px: 2.5, pt: 1, pb: 0.5 }}>
        <Box sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.secondary' }}>
          Daily
        </Box>
      </Box>
      <List dense>
        {dailyMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
  
      <Box sx={{ mx: 2, my: 1, borderTop: '1px solid #EDE8E3' }} />
  
      {/* Records */}
      <Box sx={{ px: 2.5, pb: 0.5 }}>
        <Box sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.secondary' }}>
          Records
        </Box>
      </Box>
      <List dense>
        {recordsItems.map((item) => (
          item.children ? (
            <React.Fragment key={item.text}>
              {/* Toggle only — the parent never navigates, so it never highlights. */}
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleSectionToggle(item.text)}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                  {openSections[item.text] ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={!!openSections[item.text]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.children.map((child) => (
                    <ListItem key={child.text} disablePadding>
                      <ListItemButton
                        sx={nestedItemSx}
                        selected={location.pathname === child.path}
                        onClick={() => handleNavigation(child.path)}
                      >
                        <ListItemIcon>{child.icon}</ListItemIcon>
                        <ListItemText primary={child.text} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ) : (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          )
        ))}
      </List>

      <Box sx={{ mx: 2, my: 1, borderTop: '1px solid #EDE8E3' }} />

      {/* Genetics */}
      <Box sx={{ px: 2.5, pb: 0.5 }}>
        <Box sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.secondary' }}>
          Genetics
        </Box>
      </Box>
      <List dense>
        {geneticsMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Useful Links — external sites, managed at /admin/useful-links */}
      {showUsefulLinks && (
        <>
          <Box sx={{ mx: 2, my: 1, borderTop: '1px solid #EDE8E3' }} />
          <Box sx={{ px: 2.5, pb: 0.5 }}>
            <Box sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.secondary' }}>
              Useful Links
            </Box>
          </Box>
          <List dense>
            {usefulLinks.map((link) => (
              <ListItem key={link.id} disablePadding>
                <ListItemButton
                  component="a"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleExternalLinkClick}
                >
                  <ListItemIcon><LinkIcon /></ListItemIcon>
                  <ListItemText primary={link.display_text} />
                </ListItemButton>
              </ListItem>
            ))}
            {admin && (
              <ListItem disablePadding>
                <ListItemButton
                  sx={nestedItemSx}
                  selected={location.pathname === USEFUL_LINKS_MANAGE_PATH}
                  onClick={() => handleNavigation(USEFUL_LINKS_MANAGE_PATH)}
                >
                  <ListItemIcon><SettingsIcon /></ListItemIcon>
                  <ListItemText primary="Manage Links" />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </>
      )}

      {/* Admin */}
      {admin && (
        <>
          <Box sx={{ mx: 2, my: 1, borderTop: '1px solid #EDE8E3' }} />
          <List dense>
            <ListItem disablePadding>
              <ListItemButton onClick={handleAdminToggle}>
                <ListItemIcon><AdminPanelSettingsIcon /></ListItemIcon>
                <ListItemText primary={
                  <Box sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.secondary' }}>
                    Admin
                  </Box>
                } />
                {adminOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={adminOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {adminMenuItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      sx={nestedItemSx}
                      selected={location.pathname === item.path}
                      onClick={() => handleNavigation(item.path)}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </List>
        </>
      )}
    </div>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>
      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;

